"use client";

import AngleLeftIcon from "@/assets/icons/angle-left.svg";
import AngleRightIcon from "@/assets/icons/angle-right.svg";
import DownArrowIcon from "@/assets/icons/down-arrow.svg?react";
import FavIcon from "@/assets/icons/heartIcon.svg?react";
import FavIconFilled from "@/assets/icons/heartIconFilled.svg?react";
import ShareIcon from "@/assets/icons/share.svg?react";
import SpecsIcon from "@/assets/icons/specs.svg?react";
import SpecIcon from "@/assets/icons/specsIcon.svg";
import UpArrowIcon from "@/assets/icons/up-arrow.svg?react";
import NoImage from "@/assets/images/no_image.svg";
import { cn } from "@/lib/tailwind";
import { useGlobalStore } from "@/store/store";
import { getDirFromLocale, getFieldValue, getLangFromLocale } from "@/utils/common";
import { isVideoMedia } from "@/apiUtils/productUtils";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import CldImage from "../ui/cldImg/CldImage";
import Skeleton from "../ui/skeleton/Skeleton";
import ProductImageViewer from "./ProductImageViewer/ProductImageViewer";
import { ProductDisplayCarouselProps } from "./types";
import VtoModal from "./VtoModal";
import { VideoThumbnail, VideoSlide, useVideoPlayer } from "./VideoPlayOverlay";

const ProductDisplayCarousel = ({
  altText,
  className,
  images,
  isVtoEnabled,
  frameId,
  handleShare,
  wishlistProps,
  variantId,
  isOutOfStock,
  outOfStockLabel,
  translations,
}: ProductDisplayCarouselProps & {
  isOutOfStock: boolean;
  outOfStockLabel?: string;
  altText: string;
}) => {
  const { wishlistedProducts } = useGlobalStore((state) => state);
  const [vtoOpen, setVtoOpen] = useState(false);
  const [imageViewerStatus, setImageViewerStatus] = useState(false);
  const { locale } = useParams();
  const lang = getLangFromLocale(locale as string);
  const dir = getDirFromLocale(locale as string);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, direction: dir });
  const [emblaThbRef, emblaThbApi] = useEmblaCarousel({
    loop: true,
    direction: dir,
    axis: "y",
    active: images.length > 5,
    slidesToScroll: 5,
    align: "start",
  });
  const scrollbarThumbRef = useRef<HTMLDivElement>(null);
  const [selectedThb, setSelectedThb] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Video playback — all logic lives in VideoPlayOverlay.tsx
  const { setVideoRef, playVideo, pauseAllVideos, toggleVideo, playingIdx } = useVideoPlayer();

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIdx = emblaApi.selectedScrollSnap();
    setSelectedThb(newIdx);
    if (playingIdx !== null && playingIdx !== newIdx) {
      pauseAllVideos();
    }
    const newSlide = images[newIdx];
    if (newSlide && isVideoMedia({ url: newSlide.url, type: newSlide.mediaType })) {
      setTimeout(() => playVideo(newIdx), 50);
    }
  }, [emblaApi, playingIdx, pauseAllVideos, playVideo, images]);

  // methods
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollUp = useCallback(() => {
    if (emblaThbApi) emblaThbApi.scrollPrev();
  }, [emblaThbApi]);

  const scrollDown = useCallback(() => {
    if (emblaThbApi) emblaThbApi.scrollNext();
  }, [emblaThbApi]);

  const thbHandleClick = useCallback(
    (idx: number) => {
      setSelectedThb(idx);
      if (emblaApi) emblaApi.scrollTo(idx);
      // If the clicked thumbnail is a video, play it after scroll
      const image = images[idx];
      if (image && isVideoMedia({ url: image.url, type: image.mediaType })) {
        // Small delay to let embla finish scrolling
        setTimeout(() => playVideo(idx), 50);
      } else {
        pauseAllVideos();
      }
    },
    [emblaApi, images, playVideo, pauseAllVideos],
  );

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  function handleOnload() {
    setLoaded(true);
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
  }

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
  });

  //hooks
  useEffect(() => {
    wishlistProps?.setIsWishlisted(!!wishlistedProducts?.includes(variantId || ""));
  }, []);

  return (
    <>
      <section className={cn("flex w-full gap-7 md:ps-12", className)}>
        {/* product thumbnails */}

        <div
          className={cn(
            "flex w-full sm:h-[35rem] lg:h-[39.813rem] lg:gap-[34px]",
            loaded ? "animate opacity-100 ease-in-out" : "opacity-0",
          )}
        >
          <div className={cn("hidden lg:block")}>
            {(emblaThbApi?.canScrollPrev() || emblaThbApi?.canScrollNext()) && (
              <div className="flex select-none items-center justify-center pb-3">
                <UpArrowIcon
                  role="button"
                  className="h-2.5 w-2.5 cursor-pointer"
                  onClick={() => scrollUp()}
                />
              </div>
            )}
            <div className="overflow-hidden" ref={emblaThbRef}>
              <div className="flex h-[37.2rem] flex-col gap-[12px]">
                {images.length <= 0 ? (
                  <div
                    className={cn(
                      "flex min-h-0 w-[5.5rem] flex-auto cursor-pointer items-center justify-center bg-tertiary-5",
                    )}
                    style={{ flex: "0 0 6.625rem" }}
                    role="button"
                  >
                    <Image
                      src={NoImage}
                      alt={altText || "No product image"}
                      width={88}
                      height={106}
                      className="w-auto invert-[2%]"
                    />
                  </div>
                ) : (
                  images?.map((image, idx) => {
                    const isVideo = isVideoMedia({ url: image.url, type: image.mediaType });
                    return (
                      <div
                        key={idx}
                        className={cn(
                          "relative flex min-h-0 w-[5.5rem] flex-auto cursor-pointer items-center justify-center overflow-hidden bg-tertiary-5",
                          selectedThb === idx ? "border" : "",
                        )}
                        style={{ flex: "0 0 6.625rem" }}
                        role="button"
                        onClick={() => thbHandleClick(idx)}
                      >
                        {isVideo ? (
                          <VideoThumbnail src={image.url} />
                        ) : (
                          <CldImage
                            src={image ? image.url : NoImage.src}
                            alt={altText}
                            width={88}
                            height={106}
                            className="w-auto invert-[2%]"
                          />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            {(emblaThbApi?.canScrollPrev() || emblaThbApi?.canScrollNext()) && (
              <div className="flex select-none items-center justify-center pt-3">
                <DownArrowIcon
                  role="button"
                  className="h-2.5 w-2.5 hover:cursor-pointer"
                  onClick={() => scrollDown()}
                />
              </div>
            )}
          </div>

          {/* product image carousel */}
          <div className={cn("relative w-full")}>
            <div className="w-full overflow-hidden" ref={emblaRef}>
              <div className="flex gap-1 bg-tertiary-5">
                {images.length <= 0 ? (
                  <div
                    key={"product_img_"}
                    className="flex h-[28.125rem] items-center justify-center bg-tertiary-5 sm:h-[35rem] lg:h-[39.813rem]"
                    style={{ flex: "0 0 100%" }}
                  >
                    <Image
                      src={NoImage}
                      alt={altText || "No product image"}
                      width={531}
                      height={637}
                      className="h-full w-full"
                      unoptimized={false}
                      quality={75}
                      onLoad={() => handleOnload()}
                    />
                  </div>
                ) : (
                  images?.map((item, idx) => {
                    const isVideo = isVideoMedia({ url: item.url, type: item.mediaType });
                    const isPlaying = playingIdx === idx;
                    return (
                      <div
                        key={"product_img_" + idx}
                        className="relative flex h-[28.125rem] items-center justify-center bg-tertiary-5 sm:h-[35rem] lg:h-[39.813rem]"
                        style={{ flex: "0 0 100%" }}
                        onClick={isVideo ? undefined : () => setImageViewerStatus(true)}
                      >
                        {isVideo ? (
                          <VideoSlide
                            src={item.url}
                            isPlaying={playingIdx === idx || selectedThb !== idx}
                            onToggle={(e) => toggleVideo(idx, e)}
                            onLoaded={handleOnload}
                            videoRef={setVideoRef(idx)}
                          />
                        ) : (
                          <CldImage
                            src={item ? item?.url : NoImage.src}
                            alt={altText}
                            width={531}
                            height={637}
                            className="w-auto cursor-zoom-in invert-[2%]"
                            unoptimized={false}
                            quality={75}
                            onLoad={() => handleOnload()}
                            onClick={() => setImageViewerStatus(true)}
                          />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Scrollbar for mobile */}
            {images && images.length > 1 && (
              <div className={cn("flex transform items-center duration-300 ease-in-out md:hidden")}>
                {scrollSnaps.map((snap, i) => (
                  <div
                    key={i}
                    onClick={() => {
                      onDotButtonClick(i);
                    }}
                    className={cn(
                      "flex-1",
                      selectedThb === i ? "h-[3px] bg-black" : "h-[1px] bg-[rgba(212,212,212,1)]",
                    )}
                  ></div>
                ))}
              </div>
            )}

            {/* controls over carousel  */}
            <button
              className="absolute right-5 top-5 flex h-8 w-8 min-w-0 items-center justify-center border-0 bg-white active:bg-tertiary-5 md:hidden"
              onClick={
                !wishlistProps?.isWishlisted
                  ? () => wishlistProps?.handleAddToWishlist()
                  : () => wishlistProps?.handleRemoveFromWishlist()
              }
            >
              {/* <Image src={FavIcon} alt="fav" width={20} height={20} className="h-5 w-5" /> */}
              {!wishlistProps?.isWishlisted ? (
                <FavIcon
                  className="h-[18px] w-5"
                  // @ts-ignore
                  title={getFieldValue(lang, "Favorite button", "الزر المفضل")}
                />
              ) : (
                <FavIconFilled
                  className="h-[18px] w-5"
                  // @ts-ignore
                  title={getFieldValue(lang, "Favorite button", "الزر المفضل")}
                />
              )}
            </button>

            <button
              className="absolute bottom-5 right-5 flex h-8 w-8 min-w-0 items-center justify-center border-0 bg-white active:bg-tertiary-5 md:hidden"
              onClick={() => handleShare()}
            >
              <ShareIcon
                className="h-5 w-5"
                // @ts-ignore
                title={getFieldValue(lang, "Share button", "زر المشاركة")}
              />
              {/* <Image src={ShareIcon} alt="fav" width={20} height={20} className="h-5 w-5" /> */}
            </button>

            {isVtoEnabled && (
              <button
                className="absolute bottom-5 left-5 flex items-center gap-2 bg-white px-3 py-1.5 active:bg-tertiary-5"
                onClick={() => setVtoOpen(true)}
              >
                <span className="text-sm font-semibold">
                  {translations?.common?.common_try_them_on_label ?? "Try them on"}
                </span>
                <Image src={SpecIcon} alt="fav" width={20} height={20} className="h-5 w-5" />
              </button>
            )}

            {/* Carousel contorls */}
            <div className="absolute bottom-5 right-5 hidden items-center gap-3 md:flex" dir="ltr">
              <button
                className="flex items-center bg-white p-2.5 active:bg-tertiary-5"
                onClick={() => scrollPrev()}
              >
                <Image
                  src={AngleLeftIcon}
                  alt={lang === "en" ? "Previous" : "سابق"}
                  width={20}
                  height={20}
                  className="h-3.5 w-3.5"
                />
              </button>
              <button
                className={cn(
                  "flex items-center justify-center bg-white p-2.5 active:bg-tertiary-5",
                )}
                onClick={() => scrollNext()}
              >
                <Image
                  src={AngleRightIcon}
                  alt={lang === "en" ? "Next" : "التالي"}
                  width={20}
                  height={20}
                  className="h-3.5 w-3.5"
                />
              </button>
            </div>
          </div>
        </div>

        <div
          className={cn(
            "absolute left-0 top-0 flex w-full sm:h-[35rem] md:ps-12 lg:h-[39.813rem] lg:gap-8",
            loaded ? "hidden" : "",
          )}
        >
          <div className="hidden h-[37.2rem] w-[5.5rem] flex-col gap-4 lg:flex">
            {new Array(4).fill(0)?.map((v, idx) => (
              <Skeleton key={idx} className="h-[6.625rem] w-[5.5rem] bg-tertiary-5">
                <SpecsIcon />
              </Skeleton>
            ))}
          </div>

          <Skeleton className="flex h-[28.125rem] flex-1 items-center justify-center overflow-hidden bg-tertiary-5 p-0 sm:h-[35rem] lg:h-[39.813rem]">
            <div className="flex w-full items-center justify-center bg-tertiary-5">
              <div className="absolute">
                <SpecsIcon className="w-full" />
              </div>
            </div>
          </Skeleton>
        </div>
      </section>

      <ProductImageViewer
        isOpen={imageViewerStatus}
        onClose={setImageViewerStatus}
        images={images}
        dir={dir}
        altText={altText}
        selected={selectedThb}
        translations={translations}
      />

      <VtoModal frameId={frameId} isOpen={vtoOpen} toggle={() => setVtoOpen(false)} />
    </>
  );
};

export default ProductDisplayCarousel;
